import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Breadcrumb, Skeleton, Empty, Alert } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import BannerBar from "../../../components/Bannerbar";
import RenderHelmet from "../../../components/RenderHelmet";
import {
  getElementoPorUrl,
  getBreadcrumb,
} from "../../../services/constituicaoAPI";
import style from "./style.module.css";

function getLinkForElement(el) {
  if (!el) return "#";
  if (el.url) return `/legislacao/federal/${el.url}`;
  return `/legislacao/federal/elemento/${el.id}`;
}

function ElementoFilho({ item }) {
  const to = getLinkForElement(item);
  const isArtigo = item.tipo === "artigo";
  const preview = item.texto ? (item.texto.length > 120 ? item.texto.substring(0, 120) + "..." : item.texto) : "";

  return (
    <div className={style.filhoItem}>
      <Link to={to} className={style.filhoLink}>
        {item.titulo} {isArtigo ? "" : " -"}
      </Link>
      {preview && <div className={style.filhoTexto}>{preview}</div>}
    </div>
  );
}

function ArtigoCompleto({ artigo }) {
  if (!artigo) return null;
  const filhos = artigo.filhos || [];
  const incisos = filhos.filter((f) => f.tipo === "inciso");
  const paragrafos = filhos.filter((f) => f.tipo === "paragrafo");
  const alineas = filhos.filter((f) => f.tipo === "alinea");

  return (
    <div className={style.artigoContainer}>
      <div className={style.artigoCabecalho}>
        {artigo.titulo} {artigo.texto && artigo.texto.trim()}
      </div>
      {incisos.map((inc, i) => (
        <div key={inc.id || i} className={style.incisoItem}>
          <span className={style.incisoLabel}>{inc.titulo}</span> {inc.texto}
        </div>
      ))}
      {paragrafos.map((p, i) => (
        <div key={p.id || i} className={style.paragrafoItem}>
          <strong>{p.titulo}</strong> {p.texto}
        </div>
      ))}
      {alineas.length > 0 && incisos.length === 0 && paragrafos.length === 0 &&
        filhos.map((f, i) => (
          <div key={f.id || i} className={style.incisoItem}>
            <span className={style.incisoLabel}>{f.titulo}</span> {f.texto}
          </div>
        ))}
    </div>
  );
}

export default function ConstituicaoElemento() {
  const { urlSlug } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [elemento, setElemento] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);

  useEffect(() => {
    async function load() {
      if (!urlSlug) return;
      try {
        setLoading(true);
        setError(null);

        const data = await getElementoPorUrl(urlSlug);

        setElemento(data);
        const idParaBreadcrumb = data?.id;

        if (idParaBreadcrumb) {
          try {
            const bc = await getBreadcrumb(idParaBreadcrumb);
            setBreadcrumb(bc?.data || []);
          } catch {
            setBreadcrumb([]);
          }
        }
      } catch (err) {
        setError(err?.message || "Erro ao carregar conteúdo");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [urlSlug]);

  if (loading) {
    return (
      <>
        <BannerBar customText="/constituição" />
        <div className="container mt-5 mb-5">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <BannerBar customText="/constituição" />
        <div className="container mt-5 mb-5">
          <Alert type="error" message={error} description="Verifique o endereço ou tente novamente." showIcon />
          <Link to="/legislacao/federal">← Voltar ao sumário</Link>
        </div>
      </>
    );
  }

  if (!elemento) {
    return (
      <>
        <BannerBar customText="/constituição" />
        <div className="container mt-5 mb-5">
          <Empty description="Conteúdo não encontrado" />
          <Link to="/legislacao/federal">← Voltar ao sumário</Link>
        </div>
      </>
    );
  }

  const filhos = elemento.filhos || [];
  const isArtigo = elemento.tipo === "artigo";
  const tituloPagina = [elemento.titulo, elemento.texto].filter(Boolean).join(" ") || "Constituição Federal";

  return (
    <>
      <BannerBar customText="/constituição" />
      <div className="container mt-5 mb-5">
        <RenderHelmet
          title={`${tituloPagina} - Constituição Federal - Cadê Direito`}
          description={`${tituloPagina} - Constituição da República Federativa do Brasil de 1988`}
        />

        <div className={style.breadcrumbWrap}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/"><HomeOutlined /></Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/legislacao/federal">Constituição Federal</Link>
            </Breadcrumb.Item>
            {breadcrumb.slice(0, -1).map((b) => (
              <Breadcrumb.Item key={b.id}>
                <Link to={getLinkForElement(b)}>{b.titulo}</Link>
              </Breadcrumb.Item>
            ))}
            <Breadcrumb.Item>{elemento.titulo}</Breadcrumb.Item>
          </Breadcrumb>
        </div>

        <div className={style.conteudoWrap}>
          <h2 className={style.elementoTitulo}>
            {elemento.titulo} {elemento.texto && !isArtigo && elemento.texto.trim()}
          </h2>

          {isArtigo ? (
            <ArtigoCompleto artigo={elemento} />
          ) : (
            <>
              {elemento.texto && (
                <p className={style.elementoTexto}>{elemento.texto}</p>
              )}
              {filhos.length > 0 && (
                <div className={style.filhosLista}>
                  {filhos.map((f) => (
                    <ElementoFilho key={f.id} item={f} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
